import { useEffect, useRef, useState } from "preact/hooks";
import { defaultAppSettings } from "../settings/appSettings.js";
import { defaultProfileSettings } from "../settings/profileSettings.js";
import {
  getAppSettings,
  getActiveBudgetScope,
  getProfileSettings,
  initializeWalletWatchDb,
  listBudgetRows,
  listExpenseRows,
  listProfileYears,
  listProfiles
} from "../storage/walletWatchDb.js";

function sortScopeYearsAscending(years) {
  return years
    .slice()
    .sort((left, right) => String(left).localeCompare(String(right)));
}

export function useWalletWatchBootstrap({ initialRoute }) {
  const [budgetRows, setBudgetRows] = useState([]);
  const [expenseRows, setExpenseRows] = useState([]);
  const [appSettings, setAppSettings] = useState(defaultAppSettings);
  const [budgetScope, setBudgetScope] = useState(null);
  const [availableScopeYears, setAvailableScopeYears] = useState([]);
  const [profileSettings, setProfileSettings] = useState(defaultProfileSettings);
  const [isBudgetReady, setIsBudgetReady] = useState(false);
  const [shouldShowWelcome, setShouldShowWelcome] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(null);
  const budgetRowsRef = useRef([]);
  const startedAtBareRootRef = useRef(initialRoute.isBareRoot);

  useEffect(() => {
    budgetRowsRef.current = budgetRows;
  }, [budgetRows]);

  async function fetchBudgetRows(scopeOverride) {
    const scope = scopeOverride ?? budgetScope ?? await getActiveBudgetScope();
    const rows = await listBudgetRows(scope.profileId, scope.year);
    return { scope, rows };
  }

  async function reloadBudgetRows(scopeOverride) {
    const { scope, rows } = await fetchBudgetRows(scopeOverride);
    setBudgetScope(scope);
    budgetRowsRef.current = rows;
    setBudgetRows(rows);
    return rows;
  }

  async function fetchExpenseRows(scopeOverride) {
    const scope = scopeOverride ?? budgetScope ?? await getActiveBudgetScope();
    const rows = await listExpenseRows(scope.profileId, scope.year);
    return { scope, rows };
  }

  async function reloadExpenseRows(scopeOverride) {
    const { rows } = await fetchExpenseRows(scopeOverride);
    setExpenseRows(rows);
    return rows;
  }

  async function reloadAvailableScopeYears(profileId) {
    return listProfileYears(profileId);
  }

  useEffect(() => {
    let isMounted = true;

    async function loadBudgetData() {
      await initializeWalletWatchDb();
      const scope = await getActiveBudgetScope();
      const rows = await listBudgetRows(scope.profileId, scope.year);
      const [settings, nextAppSettings] = await Promise.all([
        getProfileSettings(scope.profileId),
        getAppSettings()
      ]);
      const nextExpenseRows = await listExpenseRows(scope.profileId, scope.year);
      const [years, profiles] = await Promise.all([
        reloadAvailableScopeYears(scope.profileId),
        listProfiles()
      ]);
      const isEmptyDatabase = profiles.length === 1
        && years.length === 1
        && rows.length === 0
        && nextExpenseRows.length === 0;

      if (!isMounted) {
        return;
      }

      setAppSettings(nextAppSettings);
      setBudgetScope(scope);
      setExpenseRows(nextExpenseRows);
      setProfileSettings(settings);
      setAvailableScopeYears(sortScopeYearsAscending(years.map((entry) => entry.year)));
      budgetRowsRef.current = rows;
      setBudgetRows(rows);
      setIsBudgetReady(true);
      setShouldShowWelcome(startedAtBareRootRef.current && isEmptyDatabase);
      setBootstrapError(null);
    }

    loadBudgetData().catch((error) => {
      console.error("Failed to load wallet data.", error);
      if (isMounted) {
        setBootstrapError(error);
        setIsBudgetReady(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    appSettings,
    availableScopeYears,
    budgetRows,
    budgetRowsRef,
    budgetScope,
    expenseRows,
    isBudgetReady,
    profileSettings,
    bootstrapError,
    reloadAvailableScopeYears,
    reloadBudgetRows,
    reloadExpenseRows,
    setAppSettings,
    setAvailableScopeYears,
    setBudgetRows,
    setBudgetScope,
    setExpenseRows,
    setProfileSettings,
    setShouldShowWelcome,
    shouldShowWelcome
  };
}
